import { useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play, Send, Square, Trash2 } from "lucide-react";

import type { useAudioRecording } from "@/hooks/use-audio-recording";
import type { ConversationAudio } from "@/types/conversation";
import { Button } from "@/components/ui/button";

export function ComposerAudio({
	audio,
	recording,
	sending,
	disabled,
	onSend,
}: {
	audio: ConversationAudio | null;
	recording: ReturnType<typeof useAudioRecording>;
	sending: boolean;
	disabled: boolean;
	onSend: () => void;
}) {
	const player = useRef<HTMLAudioElement>(null);
	const [playing, setPlaying] = useState(false);
	const [position, setPosition] = useState(0);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		setReady(false);
		setPosition(0);
		setPlaying(false);

		if (!audio) {
			return;
		}

		const timer = setTimeout(() => setReady(true), Math.max(0, audio.sendAvailableAt - Date.now()));

		return () => clearTimeout(timer);
	}, [audio]);

	useEffect(() => {
		const element = player.current;

		return () => element?.pause();
	}, [audio]);

	const seconds = audio ? audio.duration : recording.seconds;
	const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

	return (
		<div className="px-3 pb-3">
			<div className="flex h-10 items-center justify-between text-xs">
				<span className="font-medium">Áudio</span>
				{recording.phase === "requesting" && (
					<span className="text-muted-foreground">Aguardando microfone...</span>
				)}
			</div>
			<div className="flex h-16 items-center gap-3 rounded-lg border border-primary/15 bg-primary-soft/50 px-2">
				<Button
					size="icon"
					variant="ghost"
					aria-label="Descartar áudio"
					disabled={sending}
					onClick={recording.discard}
				>
					<Trash2 />
				</Button>
				{audio && (
					<>
						<audio
							ref={player}
							src={audio.url}
							onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
							onPlay={() => setPlaying(true)}
							onPause={() => setPlaying(false)}
							onEnded={() => setPlaying(false)}
						/>
						<Button
							size="icon"
							variant="ghost"
							aria-label={playing ? "Pausar áudio" : "Ouvir áudio"}
							onClick={() => {
								if (playing) {
									player.current?.pause();
								} else {
									player.current?.play().catch(() => setPlaying(false));
								}
							}}
						>
							{playing && <Pause />}
							{!playing && <Play />}
						</Button>
						<input
							type="range"
							aria-label="Posição do áudio"
							min={0}
							max={audio.duration}
							step={0.1}
							value={position}
							onChange={(event) => {
								if (player.current) {
									player.current.currentTime = Number(event.target.value);
								}
								setPosition(Number(event.target.value));
							}}
							className="min-w-0 flex-1 accent-petrol"
						/>
					</>
				)}
				{!audio && (
					<div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-primary">
						<span className="size-2 animate-pulse rounded-full bg-destructive" />
						{recording.phase === "recording" && "Gravando"}
						{recording.phase !== "recording" && "Preparando áudio..."}
					</div>
				)}
				<span className="font-mono text-xs text-muted-foreground">{clock}</span>
				{!audio && (
					<Button
						size="icon"
						aria-label="Parar gravação"
						disabled={recording.phase !== "recording"}
						onClick={recording.stop}
					>
						<Square className="fill-current" />
					</Button>
				)}
				{audio && (
					<Button
						size="icon"
						aria-label={sending ? "Enviando áudio" : "Enviar áudio"}
						aria-busy={sending}
						disabled={disabled || sending || !ready}
						onClick={(event) => {
							if (event.detail < 2 && Date.now() >= audio.sendAvailableAt) {
								player.current?.pause();
								onSend();
							}
						}}
					>
						{sending && <Loader2 className="animate-spin" />}
						{!sending && <Send />}
					</Button>
				)}
			</div>
		</div>
	);
}
